'use client';

import { FormProvider, useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '../ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../Form/Input';
import SelectInput from '../Form/SelectInput';
import { useLocale, useTranslations } from 'next-intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { joinUsApi } from '@/src/app/[locale]/join-us/service/service.city';
import Image from 'next/image';
import { toast } from 'sonner';
import { CircleAlert, CircleCheck } from 'lucide-react';

const JoinForm = () => {
  const t = useTranslations('join');
  return z.object({
    storeName: z.string().min(1, { message: t('required') }),
    phoneNumber: z
      .string()
      .length(10, { message: t('phone-number-must-be-10-digits') })
      .regex(/^05[0-9]{8}$/, { message: t('the-number-must-start-with-05xxxxxxxx') }),
    email: z
      .email({
        message: t('required'),
      })
      .nonempty({ message: t('required') }),
    cityId: z.string().min(1, t('required')),
    countryId: z.string().min(1, t('required')),
    industryId: z.string().min(1, t('required')),
    instagram: z
      .string()
      .or(z.literal(''))
      .default('')
      .transform((val) => (val ? val.split('@')[1] : undefined)),
    twitter: z
      .string()
      .or(z.literal(''))
      .default('')
      .transform((val) => (val ? val.split('@')[1] : undefined)),
  });
};

export type FormInput = z.input<ReturnType<typeof JoinForm>>;
export type FormDTO = z.output<ReturnType<typeof JoinForm>>;

export const JoinUs = () => {
  const t = useTranslations('join');
  const localActive = useLocale();

  const methods = useForm<FormInput>({
    resolver: zodResolver(JoinForm()),
  });

  const { data: countryData } = useQuery({
    queryKey: ['countryData'],
    queryFn: async () => await joinUsApi.getActiveCountries(),
  });

  const getCountryId = methods.watch('countryId');

  const { data: citiesData } = useQuery({
    queryKey: ['cities', getCountryId],
    queryFn: async () => await joinUsApi.getCitiesByCountry(getCountryId),
    enabled: !!getCountryId,
  });

  const { data: getIndustries } = useQuery({
    queryKey: ['industries'],
    queryFn: async () => joinUsApi.getIndustries(),
  });

  const submitJoinUs = useMutation({
    mutationFn: async (data: FormDTO) => joinUsApi.submitJoinUs(data),
    onSuccess: () => {
      methods.reset();
      toast(t('created-successfully'), {
        icon: <CircleCheck className="text-success" />,
      });
    },
    onError: (error) => {
      toast(error.message, {
        icon: <CircleAlert className="text-warning" />,
      });
    },
  });

  const onSubmit = async (data: any) => {
    submitJoinUs.mutateAsync(data);
  };

  return (
    <div className="mx-auto flex w-full items-center justify-center px-4">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex h-full flex-col pt-[30%] pb-5 lg:mx-3 lg:w-[50%] lg:px-10 lg:pt-0"
        >
          <div className="flex gap-2 px-2 py-6 text-lg font-bold">
            {t('Join-PIK-and-be-a-part-of-our-stores')}
            <Image src="/assets/Group.svg" alt="Group" width={'30'} height={'30'} />
          </div>
          <Input name="storeName" placeholder={t('store-name')} label={t('store-name')} required />
          <Input
            name="phoneNumber"
            type="text"
            placeholder={'05xxxxxxxx'}
            label={t('phone-Number')}
            required
          />
          <Input name="email" placeholder={t('email')} label={t('email')} required />

          <SelectInput
            name={'countryId'}
            placeholder={t('PleaseChooseCountry')}
            label={t('ChooseCountry')}
            options={countryData ?? []}
            refOptionId={'id'}
            refOptionName={localActive === 'en' ? 'englishCountryName' : 'arabicCountryName'}
            required
          />

          <SelectInput
            name={'cityId'}
            placeholder={t('Please-choose-one-option')}
            label={t('city')}
            options={citiesData ?? []}
            refOptionId={'id'}
            refOptionName={localActive === 'en' ? 'englishName' : 'arabicName'}
            disabled={!getCountryId}
            required
          />
          <SelectInput
            name={'industryId'}
            placeholder={t('Please-choose-industries')}
            label={t('industries')}
            options={getIndustries ?? []}
            refOptionId={'id'}
            refOptionName={localActive === 'en' ? 'englishName' : 'arabicName'}
            required
          />
          <div className="flex w-full gap-4 py-2">
            <div className="w-full">
              <Input name="instagram" placeholder={t('Instagram')} label={`@${t('Instagram')}`} />
            </div>
            <div className="w-full">
              <Input name="twitter" placeholder={t('TwitterAcount')} label={`@${t('Twitter')}`} />
            </div>
          </div>
          <div className="w-full px-2 py-6">
            <Button
              variant="default"
              loading={submitJoinUs.isPending}
              rounded={'full'}
              className="rounded-2xl"
            >
              {t('Join')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
